import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { isEmbedded } from '../../../../../constants/config';
import { ModalKey } from '../../../../../containers/Modals/types';
import { deleteAnnotations } from '../../../../../redux/modules/annotations/actions';
import { pauseValidation } from '../../../../../redux/modules/beforeLeave/actions';
import { openModal } from '../../../../../redux/modules/modal/actions';
import { isUserViewer } from '../../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../../types/annotation';
import { State } from '../../../../../types/state';
import { SidebarFooterAction } from './utils';

type UseDeleteActionResult = {
  action: SidebarFooterAction | null;
};

export const useDeleteAction = (
  annotation: Annotation
): UseDeleteActionResult => {
  const dispatch = useDispatch();

  const intl = useIntl();

  const { restrictedAccess } = annotation;

  const annotationStatus = annotation.status;

  const isConfirming = useSelector((state: State) => state.ui.confirming);

  const isReadOnly = useSelector((state: State) => state.ui.readOnly);

  const embeddedConfig = useSelector((state: State) => state.ui.embeddedConfig);

  const userIsViewer = useSelector(isUserViewer);

  const showInformation = useCallback(
    ({
      type,
      onConfirm,
      values,
    }: {
      type: ModalKey;
      onConfirm: () => void;
      values: Record<string, unknown>;
    }) =>
      () =>
        dispatch(openModal({ textId: type, values, onConfirm })),
    [dispatch]
  );

  const showDeleteInformation = useCallback(
    (annotationToDelete: Annotation) =>
      showInformation({
        type: 'annotationDelete',
        onConfirm: () =>
          dispatch(
            pauseValidation({
              nextAction: deleteAnnotations([annotationToDelete.url]),
              trigger: 'deleteAnnotation',
              reason: 'surveys',
            })
          ),
        values: { count: 1 },
      }),
    [dispatch, showInformation]
  );

  const isDeletable =
    [
      'rejected',
      'importing',
      'failed_import',
      'confirmed',
      'exported',
      'failed_export',
    ].includes(annotationStatus) ||
    !(isConfirming || isReadOnly) ||
    restrictedAccess;

  const handleDelete = useCallback(() => {
    showDeleteInformation(annotation)();
  }, [annotation, showDeleteInformation]);

  const deleteVisible =
    !isEmbedded() ||
    (isEmbedded() && embeddedConfig?.deleteUrl && !userIsViewer);

  const result = useMemo(() => {
    return {
      action: deleteVisible
        ? {
            id: 'delete' as const,
            label: intl.formatMessage({
              id: 'components.sidebarV2.actions.delete',
            }),
            disabled: !isDeletable,
            onClick: handleDelete,
            dataCy: 'annotation-sidebar-delete',
          }
        : null,
    };
  }, [deleteVisible, handleDelete, intl, isDeletable]);

  return result;
};
